import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { webSocketStore } from '../stores/webSocketStore';
import { formatDateTime as fmtDateTime, formatTime } from '../utils/datetime';

interface CampaignStatsData {
 summary: {
  totalPlays: number;
  totalCompletes: number;
  totalScreens: number;
  totalVideos: number;
  completionRate: number;
  campaignStart: string;
  campaignEnd: string;
 };
 byVideo: Array<{
  fileHash: string;
  filename: string;
  duration: number | null;
  playCount: number;
  completeCount: number;
  stopCount: number;
  errorCount: number;
  screenCount: number;
  completionRate: number;
 }>;
 byScreen: Array<{
  clientHash: string;
  clientName: string;
  playCount: number;
  completeCount: number;
 }>;
}

interface VideoInfo {
 fileHash: string;
 filename: string;
 thumbnailPath?: string;
 fileType?: string;
 fileUrl?: string;
 duration?: number;
}

interface CampaignDetail {
 campaignHash: string;
 title: string;
 isActive: boolean;
 advertiserMoPeerHash?: string;
 advertiserBusinessNumber?: string;
 advertiserName: string;
 advertiserEmail?: string;
 advertiserPhone?: string;
 ownerMoPeerHash: string;
 moFamilyHash: string;
 moFamilyName?: string;
 startDatetime: string;
 endDatetime: string;
 budget: number;
 budgetSpent?: number;
 slotCount?: number;
 screenCount?: number;
 videos?: string[];
 createdAt: string;
 updatedAt: string;
}

const CampaignDetail: React.FC = () => {
 const { campaignHash } = useParams();
 const navigate = useNavigate();
 const [campaign, setCampaign] = useState<CampaignDetail | null>(null);
 const [isLoading, setIsLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [showStats, setShowStats] = useState(false);
 const [stats, setStats] = useState<CampaignStatsData | null>(null);
 const [statsLoading, setStatsLoading] = useState(false);
 const [statsFetchedAt, setStatsFetchedAt] = useState<Date | null>(null);
 const [statsCountdown, setStatsCountdown] = useState(300);
 const statsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
 const [videoInfos, setVideoInfos] = useState<VideoInfo[]>([]);
 const [playingVideo, setPlayingVideo] = useState<VideoInfo | null>(null);

 useEffect(() => {
 if (campaignHash) {
 loadCampaign();
 }
 }, [campaignHash]);

 const loadCampaign = async () => {
 setIsLoading(true);
 try {
 const response = await fetch(`/campaign/${campaignHash}`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 setCampaign(data.data);
 } else {
 setError('캠페인 정보를 불러오는데 실패했습니다.');
 }
 } catch (error) {
 console.error('캠페인 로드 오류:', error);
 setError('캠페인 정보를 불러오는데 실패했습니다.');
 } finally {
 setIsLoading(false);
 }
 };

 const loadVideoInfos = async (videoHashes: string[]) => {
  const infos: VideoInfo[] = [];
  for (const fileHash of videoHashes) {
   try {
    const response = await fetch(`/mofile/${fileHash}/info`, { credentials: 'include' });
    if (response.ok) {
     const data = await response.json();
     const d = data.data;
     infos.push({
      fileHash,
      filename: d?.title || fileHash.slice(0, 12),
      thumbnailPath: d?.thumbnail_path,
      fileType: d?.file_type,
      fileUrl: `/mofile/download/${fileHash}`,
      duration: d?.duration,
     });
    } else {
     infos.push({ fileHash, filename: fileHash.slice(0, 12) });
    }
   } catch {
    infos.push({ fileHash, filename: fileHash.slice(0, 12) });
   }
  }
  setVideoInfos(infos);
 };

 useEffect(() => {
  if (campaign?.videos && campaign.videos.length > 0) {
   loadVideoInfos(campaign.videos);
  }
 }, [campaign]);

 const deleteCampaign = async () => {
 if (!confirm('정말로 이 캠페인을 삭제하시겠습니까?')) return;
 
 try {
 const response = await fetch(`/campaign/${campaignHash}`, {
 method: 'DELETE',
 credentials: 'include'
 });
 
 if (response.ok) {
 navigate('/campaign');
 } else {
 alert('캠페인 삭제에 실패했습니다.');
 }
 } catch (error) {
 console.error('캠페인 삭제 오류:', error);
 alert('캠페인 삭제에 실패했습니다.');
 }
 };

 // 날짜 포맷 — 중앙 헬퍼 위임
 const formatDate = (dateString: string) => fmtDateTime(dateString, {
 year: 'numeric', month: 'long', day: 'numeric',
 hour: '2-digit', minute: '2-digit',
 });

 // 숫자 포맷 (원화)
 const formatCurrency = (amount: number) => {
 return new Intl.NumberFormat('ko-KR', {
 style: 'currency',
 currency: 'KRW'
 }).format(amount);
 };

 const loadStats = useCallback(async (silent = false, flush = false) => {
  if (!silent) setStatsLoading(true);
  try {
   const params = flush ? `?flush=true&_t=${Date.now()}` : `?_t=${Date.now()}`;
   const response = await fetch(`/campaign/${campaignHash}/stats${params}`, {
    credentials: 'include',
    cache: 'no-store',
   });
   if (response.ok) {
    const data = await response.json();
    setStats(data.data);
    setStatsFetchedAt(new Date());
    setStatsCountdown(300);
   }
  } catch (error) {
   console.error('통계 로드 오류:', error);
  } finally {
   if (!silent) setStatsLoading(false);
  }
 }, [campaignHash]);

 // 카운트다운 + 자동 갱신 (fallback 300s)
 useEffect(() => {
  if (showStats && statsFetchedAt) {
   statsTimerRef.current = setInterval(() => {
    setStatsCountdown(prev => {
     if (prev <= 1) {
      loadStats(true);
      return 300;
     }
     return prev - 1;
    });
   }, 1000);
  }
  return () => {
   if (statsTimerRef.current) clearInterval(statsTimerRef.current);
  };
 }, [showStats, statsFetchedAt, loadStats]);

 // WS push: campaign_stats_updated → immediate refresh
 useEffect(() => {
  if (!showStats || !campaign?.moFamilyHash) return;

  const handler = (message: any) => {
   if (message?.payload?.moFamilyHash === campaign.moFamilyHash) {
    loadStats(true);
   }
  };

  webSocketStore.getState().registerHandler('campaign_stats_updated', handler);
  return () => {
   // Only unregister if our handler is still the active one (prevents clobbering other instances)
   const current = webSocketStore.getState().messageHandlers.get('campaign_stats_updated');
   if (current === handler) {
    webSocketStore.getState().unregisterHandler('campaign_stats_updated');
   }
  };
 }, [showStats, campaign?.moFamilyHash, loadStats]);

 const toggleStats = () => {
  if (!showStats && !stats) {
   loadStats();
  }
  setShowStats(!showStats);
 };

 // 상태 표시
 const getStatusBadge = () => {
 if (!campaign) return null;
 
 const now = new Date();
 const start = new Date(campaign.startDatetime);
 const end = new Date(campaign.endDatetime);
 
 if (!campaign.isActive) {
 return <span className="px-3 py-1 text-sm bg-gray-100 text-gray-600">비활성</span>;
 }
 
 if (now < start) {
 return <span className="px-3 py-1 text-sm bg-blue-100 text-blue-600">예정</span>;
 }
 
 if (now > end) {
 return <span className="px-3 py-1 text-sm bg-gray-100 text-gray-600">종료</span>;
 }
 
 return <span className="px-3 py-1 text-sm bg-green-100 text-green-600">진행중</span>;
 };

 if (isLoading) {
 return (
 <div className="text-center py-8">
 <div className="inline-block animate-spin h-8 w-8 rounded-full border-4 border-gray-200 border-t-gray-900"></div>
 <p className="mt-2 text-gray-600">로딩 중...</p>
 </div>
 );
 }

 if (error || !campaign) {
 return (
 <div>
 <div className="bg-red-50 text-red-800 p-4">
 {error || '캠페인을 찾을 수 없습니다.'}
 </div>
 <button
 onClick={() => navigate('/campaign')}
 className="mt-4 text-blue-600 hover:text-blue-800"
 >
 캠페인 목록으로 돌아가기
 </button>
 </div>
 );
 }

 return (
 <div>
 {/* 모바일: flex-col-reverse 로 버튼이 위, 제목이 아래에 표시되도록.
     데스크탑: flex-row 로 좌측 제목 + 우측 버튼 (기존 레이아웃 유지). */}
 <div className="flex flex-col-reverse md:flex-row md:justify-between md:items-start gap-3 mb-6">
 <div>
 <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">{campaign.title}</h1>
 <div>{getStatusBadge()}</div>
 </div>
 <div className="flex gap-2 flex-wrap">
 <button
 onClick={toggleStats}
 className={`px-4 py-2 text-white ${showStats ? 'bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'}`}
 >
 {showStats ? '리포트 닫기' : '리포트'}
 </button>
 <button
 onClick={() => navigate(`/campaign/${campaignHash}/edit`)}
 className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600"
 >
 수정
 </button>
 <button
 onClick={deleteCampaign}
 className="px-4 py-2 bg-red-500 text-white hover:bg-red-600"
 >
 삭제
 </button>
 </div>
 </div>

 <div className="space-y-6">
 {/* 광고주 정보 */}
 <div>
 <h3 className="text-lg font-medium mb-4">광고주 정보</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <div className="text-sm text-gray-500">담당자</div>
 <div className="font-medium">{campaign.advertiserName}</div>
 </div>
 {campaign.advertiserBusinessNumber && (
 <div>
 <div className="text-sm text-gray-500">사업자 등록번호</div>
 <div className="font-medium">{campaign.advertiserBusinessNumber}</div>
 </div>
 )}
 {campaign.advertiserEmail && (
 <div>
 <div className="text-sm text-gray-500">이메일</div>
 <div className="font-medium">{campaign.advertiserEmail}</div>
 </div>
 )}
 {campaign.advertiserPhone && (
 <div>
 <div className="text-sm text-gray-500">전화번호</div>
 <div className="font-medium">{campaign.advertiserPhone}</div>
 </div>
 )}
 </div>
 </div>

 {/* 캠페인 대상 */}
 <div className="border-t pt-6">
 <h3 className="text-lg font-medium mb-4">캠페인 대상</h3>
 <div className="mb-4">
 <div className="text-sm text-gray-500">모패밀리</div>
 <button
 type="button"
 onClick={() => navigate(`/mofamily/${campaign.moFamilyHash}`)}
 className="font-medium text-left hover:text-blue-600 hover:underline"
 >
 {campaign.moFamilyName || campaign.moFamilyHash}
 </button>
 </div>
 {videoInfos.length > 0 && (
 <div>
 <div className="text-sm text-gray-500 mb-2">영상 ({videoInfos.length}개)</div>
 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
  {videoInfos.map(v => (
   <div
    key={v.fileHash}
    className="border border-gray-200 rounded overflow-hidden cursor-pointer hover:border-blue-400 hover:shadow-md transition-all"
    onClick={() => setPlayingVideo(v)}
   >
    <div className="aspect-video bg-gray-100 relative">
     {v.thumbnailPath ? (
      <img
       src={`/mofile/thumbnail/${v.thumbnailPath}`}
       alt={v.filename}
       className="w-full h-full object-cover"
      />
     ) : (
      <div className="w-full h-full flex items-center justify-center">
       <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
       </svg>
      </div>
     )}
     {v.duration != null && (
      <span className="absolute bottom-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1 rounded">
       {Math.floor(v.duration / 60)}:{String(Math.floor(v.duration % 60)).padStart(2, '0')}
      </span>
     )}
    </div>
    <div className="px-2 py-1.5">
     <div className="text-sm truncate" title={v.filename}>{v.filename}</div>
    </div>
   </div>
  ))}
 </div>
 </div>
 )}
 </div>

 {/* 기간 및 예산 */}
 <div className="border-t pt-6">
 <h3 className="text-lg font-medium mb-4">기간 및 예산</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <div className="text-sm text-gray-500">시작일</div>
 <div className="font-medium">{formatDate(campaign.startDatetime)}</div>
 </div>
 <div>
 <div className="text-sm text-gray-500">종료일</div>
 <div className="font-medium">{formatDate(campaign.endDatetime)}</div>
 </div>
 <div>
 <div className="text-sm text-gray-500">예산</div>
 <div className="font-medium">{formatCurrency(campaign.budget)}</div>
 </div>
 {campaign.budgetSpent !== undefined && (
 <div>
 <div className="text-sm text-gray-500">사용 예산</div>
 <div className="font-medium">{formatCurrency(campaign.budgetSpent)}</div>
 </div>
 )}
 </div>
 </div>

 {/* 재생 통계 (인라인) */}
 {showStats && (
 <div className="border-t pt-6">
 <div className="flex items-center justify-between mb-4">
  <h3 className="text-lg font-medium">재생 통계</h3>
  {statsFetchedAt && (
   <div className="flex items-center gap-3 text-sm text-gray-500">
    <span>{formatTime(statsFetchedAt, { hour: '2-digit', minute: '2-digit', second: '2-digit' })} 기준</span>
    <span className={`tabular-nums ${statsCountdown <= 5 ? 'text-blue-600' : ''}`}>
     {statsCountdown}초 후 갱신
    </span>
    <button
     onClick={() => loadStats(true, true)}
     className="px-2 py-0.5 text-xs bg-gray-100 hover:bg-gray-200 rounded"
    >
     지금 갱신
    </button>
   </div>
  )}
 </div>
 {statsLoading ? (
  <div className="text-center py-8">
  <div className="inline-block animate-spin h-6 w-6 rounded-full border-4 border-gray-200 border-t-gray-900"></div>
  <p className="mt-2 text-gray-500 text-sm">통계를 불러오는 중...</p>
  </div>
 ) : stats ? (
  <div className="space-y-6">
  {/* 요약 카드 */}
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
   <div className="bg-blue-50 p-4 text-center">
   <div className="text-sm text-blue-600">총 재생</div>
   <div className="text-2xl font-bold text-blue-900">{stats.summary.totalPlays}</div>
   </div>
   <div className="bg-green-50 p-4 text-center">
   <div className="text-sm text-green-600">완료</div>
   <div className="text-2xl font-bold text-green-900">{stats.summary.totalCompletes}</div>
   </div>
   <div className="bg-purple-50 p-4 text-center">
   <div className="text-sm text-purple-600">스크린</div>
   <div className="text-2xl font-bold text-purple-900">{stats.summary.totalScreens}</div>
   </div>
   <div className="bg-orange-50 p-4 text-center">
   <div className="text-sm text-orange-600">완료율</div>
   <div className="text-2xl font-bold text-orange-900">{stats.summary.completionRate}%</div>
   </div>
  </div>

  {/* 영상별 통계 */}
  {stats.byVideo.length > 0 && (
   <div>
   <h4 className="text-sm font-medium text-gray-700 mb-2">영상별 통계</h4>
   <div className="overflow-x-auto">
    <table className="w-full text-sm">
    <thead>
     <tr className="bg-gray-50 text-left">
     <th className="px-3 py-2">영상</th>
     <th className="px-3 py-2 text-right">재생</th>
     <th className="px-3 py-2 text-right">완료</th>
     <th className="px-3 py-2 text-right">중단</th>
     <th className="px-3 py-2 text-right">오류</th>
     <th className="px-3 py-2 text-right">스크린</th>
     <th className="px-3 py-2 text-right">완료율</th>
     </tr>
    </thead>
    <tbody>
     {stats.byVideo.map(v => (
     <tr key={v.fileHash} className="border-t">
      <td className="px-3 py-2 max-w-[200px] truncate" title={v.filename}>{v.filename}</td>
      <td className="px-3 py-2 text-right">{v.playCount}</td>
      <td className="px-3 py-2 text-right">{v.completeCount}</td>
      <td className="px-3 py-2 text-right">{v.stopCount}</td>
      <td className="px-3 py-2 text-right text-red-600">{v.errorCount}</td>
      <td className="px-3 py-2 text-right">{v.screenCount}</td>
      <td className="px-3 py-2 text-right">{v.completionRate}%</td>
     </tr>
     ))}
    </tbody>
    </table>
   </div>
   </div>
  )}

  {/* 스크린별 통계 */}
  {stats.byScreen.length > 0 && (
   <div>
   <h4 className="text-sm font-medium text-gray-700 mb-2">스크린별 통계</h4>
   <div className="overflow-x-auto">
    <table className="w-full text-sm">
    <thead>
     <tr className="bg-gray-50 text-left">
     <th className="px-3 py-2">스크린</th>
     <th className="px-3 py-2 text-right">재생</th>
     <th className="px-3 py-2 text-right">완료</th>
     </tr>
    </thead>
    <tbody>
     {stats.byScreen.map(s => (
     <tr key={s.clientHash} className="border-t">
      <td className="px-3 py-2">{s.clientName}</td>
      <td className="px-3 py-2 text-right">{s.playCount}</td>
      <td className="px-3 py-2 text-right">{s.completeCount}</td>
     </tr>
     ))}
    </tbody>
    </table>
   </div>
   </div>
  )}

  {stats.summary.totalPlays === 0 && (
   <div className="text-center py-6 text-gray-500">
   재생 데이터가 없습니다.
   </div>
  )}
  </div>
 ) : (
  <div className="text-center py-6 text-gray-500">
  통계를 불러올 수 없습니다.
  </div>
 )}
 </div>
 )}

 {/* 메타 정보 */}
 <div className="border-t pt-6 text-sm text-gray-500">
 <div className="flex justify-between">
 <div>생성일: {formatDate(campaign.createdAt)}</div>
 <div>수정일: {formatDate(campaign.updatedAt)}</div>
 </div>
 </div>
 </div>

 <div className="mt-6">
 <button
 onClick={() => navigate('/campaign')}
 className="text-blue-600 hover:text-blue-800"
 >
 ← 캠페인 목록으로 돌아가기
 </button>
 </div>

 {/* 영상 재생 모달 */}
 {playingVideo && (
 <div
  className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50"
  onClick={(e) => { if (e.target === e.currentTarget) setPlayingVideo(null); }}
 >
  <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded shadow-lg">
   <div className="flex justify-between items-center px-4 py-3 border-b">
    <h3 className="font-medium truncate mr-4">{playingVideo.filename}</h3>
    <button
     className="text-gray-400 hover:text-gray-600 flex-shrink-0"
     onClick={() => setPlayingVideo(null)}
    >
     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
     </svg>
    </button>
   </div>
   <div className="p-4">
    {playingVideo.fileType?.startsWith('video/') || playingVideo.fileUrl ? (
     <video
      controls
      autoPlay
      className="w-full bg-black rounded"
      src={playingVideo.fileUrl}
     />
    ) : (
     <div className="aspect-video bg-gray-100 flex items-center justify-center rounded">
      <span className="text-gray-500">미리보기를 사용할 수 없습니다</span>
     </div>
    )}
   </div>
  </div>
 </div>
 )}
 </div>
 );
};

export default CampaignDetail;